import {
  type DatabaseKernelRow,
  type DatabaseWriterKernel,
  reconcileDatabaseKernelRows,
} from '@streamlain/doc-publication-contract/database-writer';
import {
  applyUpdate,
  decodeStateVector,
  type Doc,
  encodeStateAsUpdate,
  encodeStateVector,
} from 'yjs';

export type DatabaseComputePhase =
  | 'intent-capture'
  | 'cell-current-read'
  | 'partition'
  | 'size-probe'
  | 'row-reconcile'
  | 'history-before'
  | 'history-after'
  | 'stage-encode'
  | 'live-apply';
export interface DatabaseComputeSample {
  phase: DatabaseComputePhase;
  ms: number;
  rows: number;
  bytes: number;
}
export interface DatabaseComputePort {
  readonly kernel: DatabaseWriterKernel;
  readonly record?: (sample: DatabaseComputeSample) => void;
}

/** Workspace-owned engine selection. No models cross the kernel's binary/data ABI. */
const ports = new WeakMap<object, DatabaseComputePort>();
export function registerDatabaseComputePort(
  workspace: object,
  port: DatabaseComputePort
) {
  if (ports.has(workspace))
    throw new Error('DATABASE_COMPUTE_ALREADY_REGISTERED');
  ports.set(workspace, port);
  return () => {
    if (ports.get(workspace) === port) ports.delete(workspace);
  };
}
export function databaseComputePort(workspace: object) {
  return ports.get(workspace);
}
export function measureDatabaseCompute<T>(
  port: DatabaseComputePort | undefined,
  phase: DatabaseComputePhase,
  rows: number,
  work: () => T
): T {
  if (!port?.record) return work();
  const start = performance.now();
  try {
    return work();
  } finally {
    port?.record?.({ phase, rows, bytes: 0, ms: performance.now() - start });
  }
}

/** Bridge only isolated stage docs. UI live documents are applied after ACK. */
export function reconcileDatabaseRowsWithKernel(
  port: DatabaseComputePort,
  doc: Doc,
  rows: readonly DatabaseKernelRow[]
): void {
  const start = performance.now();
  const bin = encodeStateAsUpdate(doc);
  const actor = {
    clientId: doc.clientID,
    nextClock: decodeStateVector(encodeStateVector(doc)).get(doc.clientID) ?? 0,
  };
  const session = port.kernel.open(
    [{ docId: doc.guid, bin, clock: null, actor }],
    []
  );
  try {
    reconcileDatabaseKernelRows(session, doc.guid, rows);
    const update = session.delta(doc.guid);
    // This is a local edit computed by our isolated kernel under this stage's
    // actor, not a remote actor collision. Preserve the outer local transaction.
    doc.transact(() => applyUpdate(doc, update));
    port.record?.({
      phase: 'row-reconcile',
      rows: rows.length,
      bytes: update.byteLength,
      ms: performance.now() - start,
    });
  } finally {
    session.destroy();
  }
}
export function measureDatabaseRowsWithKernel(
  port: DatabaseComputePort,
  rows: readonly DatabaseKernelRow[]
): number {
  const start = performance.now();
  // Stable temporary actor makes the byte-limit decision engine-comparable;
  // this sizing document is never committed or used as a publication actor.
  const session = port.kernel.open(
    [
      {
        docId: 'sizing',
        bin: null,
        clock: null,
        actor: { clientId: 1, nextClock: 0 },
      },
    ],
    []
  );
  try {
    reconcileDatabaseKernelRows(session, 'sizing', rows);
    const bytes = session.encodedSize('sizing');
    port.record?.({
      phase: 'size-probe',
      rows: rows.length,
      bytes,
      ms: performance.now() - start,
    });
    return bytes;
  } finally {
    session.destroy();
  }
}
